import { User, Card, Transaction, Merchant, AdminActionLog, CardUpdateData, AdminTransactionData, WithdrawalRequest, DepositRequest, Ticket, LoanRequest } from '../types';
import { generateCardNumber, generateCVV, generateExpiryDate } from '../utils/cardGenerator';
import bcrypt from 'bcryptjs';

// Mock database usando localStorage
class MockDatabase {
  private users: User[] = [];
  private transactions: Transaction[] = [];
  private merchants: Merchant[] = [];
  private withdrawalRequests: WithdrawalRequest[] = [];
  private depositRequests: DepositRequest[] = [];
  private tickets: Ticket[] = [];
  private loanRequests: LoanRequest[] = [];
  private adminLogs: AdminActionLog[] = [];
  private currentUserId = 1;
  private currentTransactionId = 1;
  private currentWithdrawalId = 1;
  private currentDepositId = 1;
  private currentTicketId = 1;
  private currentLoanId = 1;
  private currentLogId = 1;

  constructor() {
    this.initializeData();
  }

  private async initializeData() {
    // Cargar datos del localStorage o inicializar
    const storedUsers = localStorage.getItem('castle_users');
    const storedTransactions = localStorage.getItem('castle_transactions');
    const storedMerchants = localStorage.getItem('castle_merchants');
    const storedWithdrawals = localStorage.getItem('castle_withdrawals');
    const storedDeposits = localStorage.getItem('castle_deposits');
    const storedTickets = localStorage.getItem('castle_tickets');
    const storedLoans = localStorage.getItem('castle_loans');
    const storedLogs = localStorage.getItem('castle_admin_logs');

    if (storedUsers) {
      this.users = JSON.parse(storedUsers);
      this.currentUserId = Math.max(...this.users.map(u => u.id), 0) + 1;
    }

    if (storedTransactions) {
      this.transactions = JSON.parse(storedTransactions);
      this.currentTransactionId = Math.max(...this.transactions.map(t => t.id), 0) + 1;
    }

    if (storedWithdrawals) {
      this.withdrawalRequests = JSON.parse(storedWithdrawals);
      this.currentWithdrawalId = Math.max(...this.withdrawalRequests.map(w => w.id), 0) + 1;
    }

    if (storedDeposits) {
      this.depositRequests = JSON.parse(storedDeposits);
      this.currentDepositId = Math.max(...this.depositRequests.map(d => d.id), 0) + 1;
    }

    if (storedTickets) {
      this.tickets = JSON.parse(storedTickets);
      this.currentTicketId = Math.max(...this.tickets.map(t => t.id), 0) + 1;
    }

    if (storedLoans) {
      this.loanRequests = JSON.parse(storedLoans);
      this.currentLoanId = Math.max(...this.loanRequests.map(l => l.id), 0) + 1;
    }

    if (storedLogs) {
      this.adminLogs = JSON.parse(storedLogs);
      this.currentLogId = Math.max(...this.adminLogs.map(l => l.id), 0) + 1;
    }

    if (storedMerchants) {
      this.merchants = JSON.parse(storedMerchants);
    } else {
      // Inicializar merchants por defecto con logos
      this.merchants = [
        { id: 1, name: 'Supermercado Nacional', label: 'supermercado', logo: 'ri-store-2-line' },
        { id: 2, name: 'Gasolinera Shell', label: 'gasolinera', logo: 'ri-gas-station-line' },
        { id: 3, name: 'Farmacia Carol', label: 'farmacia', logo: 'ri-hospital-line' },
        { id: 4, name: 'Tienda La Sirena', label: 'tienda', logo: 'ri-shopping-bag-line' },
        { id: 5, name: 'Restaurante Típico', label: 'restaurante', logo: 'ri-restaurant-line' },
        { id: 6, name: 'Banco Popular', label: 'banco', logo: 'ri-bank-line' }
      ];
      this.saveMerchants();
    }

    // Crear usuario admin si no existe
    const adminExists = this.users.find(u => u.email === 'admin@castle');
    if (!adminExists) {
      const adminPasswordHash = await bcrypt.hash('edual12', 10);
      const adminUser: User = {
        id: this.currentUserId++,
        cedula: '00000000000',
        email: 'admin@castle',
        fullName: 'Administrador CASTLE',
        passwordHash: adminPasswordHash,
        role: 'admin',
        isActive: true,
        isBanned: false,
        balance: 1000000,
        transactions: [],
        createdAt: new Date().toISOString()
      };
      
      // Crear tarjeta para admin
      const adminCard: Card = {
        id: 1,
        userId: adminUser.id,
        number: generateCardNumber(),
        holderName: adminUser.fullName,
        expiryDate: generateExpiryDate(),
        cvv: generateCVV()
      };
      
      adminUser.card = adminCard;
      this.users.push(adminUser);
      this.saveUsers();
    }
  }

  private saveUsers() {
    localStorage.setItem('castle_users', JSON.stringify(this.users));
  }

  private saveTransactions() {
    localStorage.setItem('castle_transactions', JSON.stringify(this.transactions));
  }

  private saveMerchants() {
    localStorage.setItem('castle_merchants', JSON.stringify(this.merchants));
  }

  private saveWithdrawals() {
    localStorage.setItem('castle_withdrawals', JSON.stringify(this.withdrawalRequests));
  }

  private saveDeposits() {
    localStorage.setItem('castle_deposits', JSON.stringify(this.depositRequests));
  }

  private saveTickets() {
    localStorage.setItem('castle_tickets', JSON.stringify(this.tickets));
  }

  private saveLoans() {
    localStorage.setItem('castle_loans', JSON.stringify(this.loanRequests));
  }

  private saveAdminLogs() {
    localStorage.setItem('castle_admin_logs', JSON.stringify(this.adminLogs));
  }

  // User methods
  async createUser(userData: { cedula: string; email: string; fullName: string; password: string }): Promise<User> {
    const passwordHash = await bcrypt.hash(userData.password, 10);
    
    const newUser: User = {
      id: this.currentUserId++,
      cedula: userData.cedula,
      email: userData.email,
      fullName: userData.fullName,
      passwordHash,
      role: 'user',
      isActive: true,
      isBanned: false,
      balance: 0,
      transactions: [],
      createdAt: new Date().toISOString()
    };

    // Crear tarjeta automáticamente
    const card: Card = {
      id: this.currentUserId,
      userId: newUser.id,
      number: generateCardNumber(),
      holderName: newUser.fullName,
      expiryDate: generateExpiryDate(),
      cvv: generateCVV()
    };

    newUser.card = card;
    this.users.push(newUser);
    this.saveUsers();
    
    return newUser;
  }

  async authenticateUser(email: string, password: string): Promise<User | null> {
    const user = this.users.find(u => u.email === email);
    if (!user || !user.isActive || user.isBanned) return null;
    
    const isValid = await bcrypt.compare(password, user.passwordHash);
    return isValid ? user : null;
  }

  getUserById(id: number): User | null {
    return this.users.find(u => u.id === id) || null;
  }

  getUserByEmail(email: string): User | null {
    return this.users.find(u => u.email === email) || null;
  }

  getAllUsers(): User[] {
    return this.users;
  }

  updateUser(id: number, updates: Partial<User>): User | null {
    const userIndex = this.users.findIndex(u => u.id === id);
    if (userIndex === -1) return null;
    
    this.users[userIndex] = { ...this.users[userIndex], ...updates };
    this.saveUsers();
    return this.users[userIndex];
  }

  updateUserBalance(userId: number, amount: number): boolean {
    const user = this.users.find(u => u.id === userId);
    if (!user) return false;
    
    user.balance += amount;
    this.saveUsers();
    return true;
  }

  // Nuevo método para actualizar tarjeta completa
  updateUserCard(userId: number, cardData: CardUpdateData, adminEmail: string): Card | null {
    const user = this.users.find(u => u.id === userId);
    if (!user) return null;

    // Validaciones básicas
    if (!cardData.number || !cardData.holderName) return null;
    if (cardData.number.replace(/\s/g, '').length < 12) return null;

    const updatedCard: Card = {
      id: user.card?.id || Date.now(),
      userId: userId,
      number: cardData.number,
      holderName: cardData.holderName,
      expiryDate: cardData.expiryDate,
      cvv: cardData.cvv
    };

    user.card = updatedCard;
    this.saveUsers();

    // Log de acción administrativa
    this.logAdminAction(adminEmail, 'update_card', JSON.stringify({
      userId,
      cardNumber: cardData.number.slice(-4),
      holderName: cardData.holderName
    }));

    return updatedCard;
  }

  // Nuevo método para crear transacciones administrativas
  createAdminTransaction(transactionData: AdminTransactionData, adminEmail: string): Transaction | null {
    const user = this.users.find(u => u.id === transactionData.userId);
    if (!user) return null;

    // Validaciones
    if (typeof transactionData.amount !== 'number' || !transactionData.type) return null;

    // Crear transacción
    const transaction: Transaction = {
      id: this.currentTransactionId++,
      userId: transactionData.userId,
      amount: transactionData.amount,
      type: transactionData.type,
      description: transactionData.description || 'Transacción administrativa',
      merchantId: transactionData.merchantId,
      status: 'approved',
      createdAt: new Date().toISOString()
    };

    // Actualizar balance según el tipo
    if (transactionData.type === 'deposit') {
      user.balance += Math.abs(transactionData.amount);
      transaction.amount = Math.abs(transactionData.amount);
    } else if (['withdrawal', 'payment', 'transfer'].includes(transactionData.type)) {
      user.balance -= Math.abs(transactionData.amount);
      transaction.amount = -Math.abs(transactionData.amount);
    }

    this.transactions.push(transaction);
    this.saveTransactions();
    this.saveUsers();

    // Log de acción administrativa
    this.logAdminAction(adminEmail, 'create_transaction', JSON.stringify({
      userId: transactionData.userId,
      amount: transactionData.amount,
      type: transactionData.type,
      description: transactionData.description
    }));

    return transaction;
  }

  // Método para eliminar transacciones
  deleteTransaction(transactionId: number, adminEmail: string): boolean {
    const transactionIndex = this.transactions.findIndex(t => t.id === transactionId);
    if (transactionIndex === -1) return false;

    const transaction = this.transactions[transactionIndex];
    const user = this.users.find(u => u.id === transaction.userId);
    
    if (user && transaction.status === 'approved') {
      // Revertir el balance si la transacción estaba aprobada
      if (transaction.type === 'deposit') {
        user.balance -= Math.abs(transaction.amount);
      } else if (['withdrawal', 'payment', 'transfer'].includes(transaction.type)) {
        user.balance += Math.abs(transaction.amount);
      }
      this.saveUsers();
    }

    this.transactions.splice(transactionIndex, 1);
    this.saveTransactions();

    // Log de acción administrativa
    this.logAdminAction(adminEmail, 'delete_transaction', JSON.stringify({
      transactionId,
      userId: transaction.userId,
      amount: transaction.amount,
      type: transaction.type
    }));

    return true;
  }

  // Método para editar transacciones
  updateTransaction(transactionId: number, updates: Partial<Transaction>, adminEmail: string): Transaction | null {
    const transactionIndex = this.transactions.findIndex(t => t.id === transactionId);
    if (transactionIndex === -1) return null;

    const oldTransaction = { ...this.transactions[transactionIndex] };
    this.transactions[transactionIndex] = { ...this.transactions[transactionIndex], ...updates };
    
    // Si se cambió el monto o tipo, actualizar balance
    const user = this.users.find(u => u.id === oldTransaction.userId);
    if (user && (updates.amount !== undefined || updates.type !== undefined)) {
      // Revertir transacción anterior
      if (oldTransaction.status === 'approved') {
        if (oldTransaction.type === 'deposit') {
          user.balance -= Math.abs(oldTransaction.amount);
        } else if (['withdrawal', 'payment', 'transfer'].includes(oldTransaction.type)) {
          user.balance += Math.abs(oldTransaction.amount);
        }
      }

      // Aplicar nueva transacción
      const newTransaction = this.transactions[transactionIndex];
      if (newTransaction.status === 'approved') {
        if (newTransaction.type === 'deposit') {
          user.balance += Math.abs(newTransaction.amount);
        } else if (['withdrawal', 'payment', 'transfer'].includes(newTransaction.type)) {
          user.balance -= Math.abs(newTransaction.amount);
        }
      }
      this.saveUsers();
    }

    this.saveTransactions();

    // Log de acción administrativa
    this.logAdminAction(adminEmail, 'update_transaction', JSON.stringify({
      transactionId,
      oldData: oldTransaction,
      newData: updates
    }));

    return this.transactions[transactionIndex];
  }

  // Withdrawal Request methods - UPDATED
  createWithdrawalRequest(
    userId: number, 
    amount: number, 
    accountType: string,
    holderName: string,
    holderCedula: string,
    accountNumber: string,
    description?: string
  ): WithdrawalRequest | null {
    const user = this.users.find(u => u.id === userId);
    if (!user || user.balance < amount) return null;

    const request: WithdrawalRequest = {
      id: this.currentWithdrawalId++,
      userId,
      amount,
      accountType,
      holderName,
      holderCedula,
      accountNumber,
      status: 'pending',
      description,
      createdAt: new Date().toISOString()
    };

    this.withdrawalRequests.push(request);
    this.saveWithdrawals();
    return request;
  }

  // Deposit Request methods - NEW
  createDepositRequest(
    userId: number,
    amount: number,
    receipt: File,
    note?: string
  ): DepositRequest | null {
    const user = this.users.find(u => u.id === userId);
    if (!user) return null;

    // Simular URL del comprobante (en producción sería subido a un servidor)
    const receiptUrl = `receipt_${Date.now()}_${receipt.name}`;

    const request: DepositRequest = {
      id: this.currentDepositId++,
      userId,
      amount,
      receiptUrl,
      note,
      status: 'pending',
      createdAt: new Date().toISOString()
    };

    this.depositRequests.push(request);
    this.saveDeposits();
    return request;
  }

  getAllDepositRequests(): DepositRequest[] {
    return this.depositRequests.map(d => ({
      ...d,
      user: this.users.find(u => u.id === d.userId)
    }));
  }

  getPendingDepositRequests(): DepositRequest[] {
    return this.depositRequests
      .filter(d => d.status === 'pending')
      .map(d => ({
        ...d,
        user: this.users.find(u => u.id === d.userId)
      }));
  }

  getUserDepositRequests(userId: number): DepositRequest[] {
    return this.depositRequests.filter(d => d.userId === userId);
  }

  approveDepositRequest(requestId: number, adminEmail: string): boolean {
    const request = this.depositRequests.find(d => d.id === requestId);
    if (!request || request.status !== 'pending') return false;

    const user = this.users.find(u => u.id === request.userId);
    if (!user) return false;

    // Agregar balance
    user.balance += request.amount;
    
    // Marcar como aprobado
    request.status = 'approved';

    // Crear transacción
    const transaction: Transaction = {
      id: this.currentTransactionId++,
      userId: request.userId,
      amount: request.amount,
      type: 'deposit',
      status: 'approved',
      description: `Depósito aprobado - RD$ ${request.amount.toLocaleString('es-DO', { minimumFractionDigits: 2 })}`,
      createdAt: new Date().toISOString()
    };

    this.transactions.push(transaction);
    this.saveUsers();
    this.saveDeposits();
    this.saveTransactions();

    // Log de acción administrativa
    this.logAdminAction(adminEmail, 'approve_deposit', JSON.stringify({
      requestId,
      userId: request.userId,
      amount: request.amount
    }));

    return true;
  }

  rejectDepositRequest(requestId: number, adminEmail: string): boolean {
    const request = this.depositRequests.find(d => d.id === requestId);
    if (!request || request.status !== 'pending') return false;

    request.status = 'rejected';
    this.saveDeposits();

    // Log de acción administrativa
    this.logAdminAction(adminEmail, 'reject_deposit', JSON.stringify({
      requestId,
      userId: request.userId,
      amount: request.amount
    }));

    return true;
  }

  getAllWithdrawalRequests(): WithdrawalRequest[] {
    return this.withdrawalRequests.map(w => ({
      ...w,
      user: this.users.find(u => u.id === w.userId)
    }));
  }

  getPendingWithdrawalRequests(): WithdrawalRequest[] {
    return this.withdrawalRequests
      .filter(w => w.status === 'pending')
      .map(w => ({
        ...w,
        user: this.users.find(u => u.id === w.userId)
      }));
  }

  getUserWithdrawalRequests(userId: number): WithdrawalRequest[] {
    return this.withdrawalRequests.filter(w => w.userId === userId);
  }

  approveWithdrawalRequest(requestId: number, adminEmail: string): boolean {
    const request = this.withdrawalRequests.find(w => w.id === requestId);
    if (!request || request.status !== 'pending') return false;

    const user = this.users.find(u => u.id === request.userId);
    if (!user || user.balance < request.amount) return false;

    // Descontar balance
    user.balance -= request.amount;
    
    // Marcar como aprobado
    request.status = 'approved';

    // Crear transacción
    const transaction: Transaction = {
      id: this.currentTransactionId++,
      userId: request.userId,
      amount: -request.amount,
      type: 'withdrawal',
      status: 'approved',
      description: `Retiro aprobado - ${request.bank}: ${request.description || ''}`,
      createdAt: new Date().toISOString()
    };

    this.transactions.push(transaction);
    this.saveUsers();
    this.saveWithdrawals();
    this.saveTransactions();

    // Log de acción administrativa
    this.logAdminAction(adminEmail, 'approve_withdrawal', JSON.stringify({
      requestId,
      userId: request.userId,
      amount: request.amount,
      bank: request.bank
    }));

    return true;
  }

  rejectWithdrawalRequest(requestId: number, adminEmail: string): boolean {
    const request = this.withdrawalRequests.find(w => w.id === requestId);
    if (!request || request.status !== 'pending') return false;

    request.status = 'rejected';
    this.saveWithdrawals();

    // Log de acción administrativa
    this.logAdminAction(adminEmail, 'reject_withdrawal', JSON.stringify({
      requestId,
      userId: request.userId,
      amount: request.amount,
      bank: request.bank
    }));

    return true;
  }

  // Método para registrar acciones administrativas
  logAdminAction(admin: string, action: string, details: string): void {
    const log: AdminActionLog = {
      id: this.currentLogId++,
      admin,
      action,
      details,
      timestamp: new Date().toISOString()
    };

    this.adminLogs.push(log);
    this.saveAdminLogs();
  }

  // Método para obtener logs administrativos
  getAdminLogs(): AdminActionLog[] {
    return this.adminLogs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }

  // Transaction methods
  createTransaction(transactionData: Omit<Transaction, 'id' | 'createdAt'>): Transaction {
    const transaction: Transaction = {
      ...transactionData,
      id: this.currentTransactionId++,
      createdAt: new Date().toISOString()
    };
    
    this.transactions.push(transaction);
    this.saveTransactions();
    return transaction;
  }

  getUserTransactions(userId: number): Transaction[] {
    return this.transactions.filter(t => t.userId === userId && t.status === 'approved');
  }

  getAllTransactions(): Transaction[] {
    return this.transactions;
  }

  // Merchant methods
  getAllMerchants(): Merchant[] {
    return this.merchants;
  }

  updateMerchant(id: number, updates: Partial<Merchant>): Merchant | null {
    const merchantIndex = this.merchants.findIndex(m => m.id === id);
    if (merchantIndex === -1) return null;
    
    this.merchants[merchantIndex] = { ...this.merchants[merchantIndex], ...updates };
    this.saveMerchants();
    return this.merchants[merchantIndex];
  }

  // Transfer and withdraw methods - UPDATED
  async transfer(fromUserId: number, toEmail: string, amount: number, description: string): Promise<boolean> {
    const fromUser = this.users.find(u => u.id === fromUserId);
    const toUser = this.users.find(u => u.email === toEmail);
    
    if (!fromUser || !toUser || fromUser.balance < amount) return false;
    
    // Actualizar balances
    fromUser.balance -= amount;
    toUser.balance += amount;
    
    // Crear transacciones
    this.createTransaction({
      userId: fromUserId,
      amount: -amount,
      type: 'transfer',
      description: `Transferencia a ${toUser.fullName}: ${description}`,
      status: 'approved'
    });
    
    this.createTransaction({
      userId: toUser.id,
      amount: amount,
      type: 'deposit',
      description: `Transferencia de ${fromUser.fullName}: ${description}`,
      status: 'approved'
    });
    
    this.saveUsers();
    return true;
  }

  async withdraw(
    userId: number, 
    amount: number, 
    accountType: string,
    holderName: string,
    holderCedula: string,
    accountNumber: string,
    description: string
  ): Promise<boolean> {
    const user = this.users.find(u => u.id === userId);
    if (!user || user.balance < amount) return false;
    
    // Crear solicitud de retiro en lugar de procesar inmediatamente
    const request = this.createWithdrawalRequest(
      userId, 
      amount, 
      accountType, 
      holderName, 
      holderCedula, 
      accountNumber, 
      description
    );
    return !!request;
  }

  // Ticket methods
  createTicket(userId: number, subject: 'transferencia' | 'deposito' | 'retiro' | 'otro', message?: string): Ticket | null {
    const user = this.users.find(u => u.id === userId);
    if (!user) return null;

    const ticket: Ticket = {
      id: this.currentTicketId++,
      userId,
      subject,
      message: message || undefined,
      status: 'open',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    this.tickets.push(ticket);
    this.saveTickets();
    return ticket;
  }

  getAllTickets(): Ticket[] {
    return this.tickets.map(t => ({
      ...t,
      user: this.users.find(u => u.id === t.userId)
    })).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  getUserTickets(userId: number): Ticket[] {
    return this.tickets.filter(t => t.userId === userId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  getTicketById(ticketId: number): Ticket | null {
    const ticket = this.tickets.find(t => t.id === ticketId);
    if (!ticket) return null;
    
    return {
      ...ticket,
      user: this.users.find(u => u.id === ticket.userId)
    };
  }

  updateTicket(ticketId: number, updates: Partial<Ticket>, adminEmail?: string): Ticket | null {
    const ticketIndex = this.tickets.findIndex(t => t.id === ticketId);
    if (ticketIndex === -1) return null;

    this.tickets[ticketIndex] = {
      ...this.tickets[ticketIndex],
      ...updates,
      updatedAt: new Date().toISOString()
    };

    this.saveTickets();

    if (adminEmail) {
      this.logAdminAction(adminEmail, 'update_ticket', JSON.stringify({
        ticketId,
        updates
      }));
    }

    return this.tickets[ticketIndex];
  }

  // Loan Request methods
  createLoanRequest(loanData: Omit<LoanRequest, 'id' | 'createdAt' | 'updatedAt' | 'status'>): LoanRequest | null {
    const user = this.users.find(u => u.id === loanData.userId);
    if (!user) return null;

    const loan: LoanRequest = {
      ...loanData,
      id: this.currentLoanId++,
      status: 'pending',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    this.loanRequests.push(loan);
    this.saveLoans();
    return loan;
  }

  getAllLoanRequests(): LoanRequest[] {
    return this.loanRequests.map(l => ({
      ...l,
      user: this.users.find(u => u.id === l.userId)
    })).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  getPendingLoanRequests(): LoanRequest[] {
    return this.loanRequests
      .filter(l => l.status === 'pending')
      .map(l => ({
        ...l,
        user: this.users.find(u => u.id === l.userId)
      }))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  getUserLoanRequests(userId: number): LoanRequest[] {
    return this.loanRequests.filter(l => l.userId === userId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  getLoanRequestById(loanId: number): LoanRequest | null {
    const loan = this.loanRequests.find(l => l.id === loanId);
    if (!loan) return null;
    
    return {
      ...loan,
      user: this.users.find(u => u.id === loan.userId)
    };
  }

  approveLoanRequest(loanId: number, adminEmail: string): boolean {
    const loan = this.loanRequests.find(l => l.id === loanId);
    if (!loan || loan.status !== 'pending') return false;

    const user = this.users.find(u => u.id === loan.userId);
    if (!user) return false;

    // Agregar el monto del préstamo al balance del usuario
    user.balance += loan.amount;
    loan.status = 'approved';
    loan.updatedAt = new Date().toISOString();

    // Crear transacción
    const transaction: Transaction = {
      id: this.currentTransactionId++,
      userId: loan.userId,
      amount: loan.amount,
      type: 'deposit',
      status: 'approved',
      description: `Préstamo aprobado - RD$ ${loan.amount.toLocaleString('es-DO', { minimumFractionDigits: 2 })}`,
      createdAt: new Date().toISOString()
    };

    this.transactions.push(transaction);
    this.saveUsers();
    this.saveLoans();
    this.saveTransactions();

    this.logAdminAction(adminEmail, 'approve_loan', JSON.stringify({
      loanId,
      userId: loan.userId,
      amount: loan.amount
    }));

    return true;
  }

  rejectLoanRequest(loanId: number, adminEmail: string): boolean {
    const loan = this.loanRequests.find(l => l.id === loanId);
    if (!loan || loan.status !== 'pending') return false;

    loan.status = 'rejected';
    loan.updatedAt = new Date().toISOString();
    this.saveLoans();

    this.logAdminAction(adminEmail, 'reject_loan', JSON.stringify({
      loanId,
      userId: loan.userId,
      amount: loan.amount
    }));

    return true;
  }
}

export const mockDB = new MockDatabase();
